import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING,
} from '../GuideKit';

/* Operations → Client Information Change → Address / Contact Update.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Address / Contact Update Screen"
 * (region "Address / Contact Update Details", with the Phone / Email, Address,
 * beneficiary repeater and Trustee / Trusted Contact sections) and "Add Address
 * / Contact Update Files" (upload tiles, notice, Acknowledgement). The other two
 * Client Information Change sub-categories use different screens and have
 * their own guides. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Client Information Change',
    subCategory: 'Address / Contact Update',
    subject: subjectRow('Address update - 0989-022356'),
    lookups: [['Household', 'The client household. The case is stamped with it, and the advisor and office are taken from it.']],
    note: (
      <>
        Investment Objective / Risk Update and Banking / ACH Instructions Update are different forms with their own
        guides - this one covers Address / Contact Update only.
      </>
    ),
  }),
  {
    title: 'Tick what is changing and fill in the details',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Address / Contact Update Details</strong> screen starts with{' '}
          <em>Please select the update categories...</em> - tick every kind of change in this request. Each box you
          tick opens its own section further down the same screen, so one case can cover a new address, a new
          phone number and a beneficiary change together.
        </p>
        <p style={{ margin: 0 }}>
          Enter the client&rsquo;s first and last name, then fill in{' '}
          <em>Please enter all relevant information for the Operations Team...</em> It becomes the case description
          Operations reads first, so say which accounts are affected and anything the sections below do not cover.
        </p>
      </>
    ),
  },
  {
    title: 'Add each beneficiary (if you ticked Beneficiaries)',
    body: (
      <p style={{ margin: 0 }}>
        The beneficiary fields are a repeating block - fill in one block per beneficiary and use the add button
        under it to start another. Every block needs its own type, relationship, percent, name, phone and email.
        Use the <em>Account</em> line to say which accounts the beneficiary applies to, or enter ALL.
      </p>
    ),
  },
  {
    title: 'Attach documents, acknowledge and submit',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Click Next to reach <strong>Attach Address / Contact Update / Beneficiary / Trustee / Trusted Contact
          Files</strong>. Add the signed forms and supporting documents to the upload tiles. The screen warns:{' '}
          <em>Please note, missing or incomplete documents may result in a NIGO.</em>
        </p>
        <p style={{ margin: 0 }}>
          Read the Operations and Trading notice under the tiles, select <em>I have read, understand, and agree to
          the above information</em> under Acknowledgement and click Next. FSC creates the case, attaches your files
          and opens it. Note the case number for follow-up.
        </p>
      </>
    ),
  },
];

// Every question on the Address / Contact Update screens, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Address / Contact Update Details',
    q: 'Please select the update categories...',
    when: 'Always - this is the first question',
    options: ['Address', 'Phone', 'Email', 'Beneficiaries', 'Trustee', 'Trusted Contact', 'Other'],
    note: 'Tick all that apply. Each choice except Other opens its own section below.',
  },
  {
    q: 'Other Details...',
    when: 'Only if you ticked Other',
    note: 'Say what the other change is.',
  },
  {
    q: 'Client First Name · Client Last Name',
    when: 'Always',
    note: 'The client the change is for.',
  },
  {
    q: 'Please enter all relevant information for the Operations Team...',
    when: 'Always',
    note: 'Becomes the case description. Include which accounts are affected and anything not captured elsewhere on the form.',
  },
  {
    screen: 'Phone / Email Update Details',
    q: 'Phone · Email',
    when: 'The phone field if you ticked Phone, the email field if you ticked Email',
    note: 'Enter the new number or address. Each is required once it appears.',
  },
  {
    screen: 'Address Update Details',
    q: 'Address',
    when: 'Only if you ticked Address',
    note: 'A standard address block for the client’s new address. Required once it appears.',
  },
  {
    screen: 'Beneficiaries - one block per beneficiary',
    q: 'Beneficiary Type',
    when: 'Only if you ticked Beneficiaries',
    note: 'Pick from the list. The field’s help text explains Individual, Primary and Contingent.',
  },
  {
    q: 'Relationship (to Owner)',
    when: 'In each beneficiary block',
  },
  {
    q: 'Percent',
    when: 'In each beneficiary block',
    note: 'The beneficiary’s share, entered as a number.',
  },
  {
    q: 'Per Stirpes',
    when: 'In each beneficiary block',
    note: 'Ticked by default. The field’s help text explains per stirpes and the alternative, per capita, with a worked example.',
  },
  {
    q: 'Beneficiary First Name · Beneficiary Last Name',
    when: 'In each beneficiary block',
  },
  {
    q: 'Beneficiary DOB · Beneficiary SSN',
    when: 'In each beneficiary block',
    note: 'Optional - the only optional fields in the block apart from Account.',
  },
  {
    q: 'Beneficiary Phone · Beneficiary Email',
    when: 'In each beneficiary block',
    note: 'Both required for every beneficiary. You cannot leave the screen until each block has them.',
    warn: true,
  },
  {
    q: 'Account (If for all accounts, enter "ALL", otherwise, list each Account you wish to add the Beneficiary to)',
    when: 'In each beneficiary block',
    note: 'Optional, but leaving it blank leaves Operations to guess. List the account numbers, or enter ALL.',
  },
  {
    screen: 'Trustee / Trusted Contact Details',
    q: 'Trustee / Trusted Contact First Name · Trustee / Trusted Contact Last Name',
    when: 'Only if you ticked Trustee or Trusted Contact',
    note: 'One section is shared by both. A note under it explains, under FINRA Rule 4512, what a Trusted Contact is: someone the firm may reach out to, who cannot transact, view balances or make decisions on the account.',
  },
  {
    q: 'Trustee / Trusted Contact Phone · Trustee / Trusted Contact Email',
    when: 'In the Trustee / Trusted Contact section',
  },
  {
    q: 'Trustee / Trusted Contact Relationship (To Owner)',
    when: 'In the Trustee / Trusted Contact section',
  },
  {
    q: 'Trustee / Trusted Contact Additional Details',
    when: 'In the Trustee / Trusted Contact section',
    note: 'Required. Say whether this person is a trustee or a trusted contact, and which accounts it covers.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Client Information Change'),
  {
    h: "A section I expected hasn't appeared",
    p: [
      'The Phone / Email, Address, beneficiary and Trustee / Trusted Contact sections only appear when the matching box is ticked in Please select the update categories... Tick the box and the section opens on the same screen.',
    ],
  },
  {
    h: "Next won't take me past the first screen",
    p: [
      'Every field in an open section is required except Beneficiary DOB, Beneficiary SSN and the beneficiary Account line. The usual culprits are a beneficiary block missing its phone or email, or an empty Trustee / Trusted Contact Additional Details.',
      'If you added a beneficiary block by mistake, remove it rather than leaving it blank.',
    ],
  },
  {
    h: 'I picked the wrong household',
    p: [
      'The case, and any beneficiary records it creates, are stamped with the household chosen on the first screen. If it is wrong, cancel and start again rather than working around it.',
    ],
  },
];

export default function ClientInfoAddressContact() {
  return (
    <GuidePage
      title="Address and Contact Updates"
      icon="person-lines-fill"
      path="Operations → Client Information Change → Address / Contact Update"
      minutes={6}
      intro="How to ask the Prosperity Operations team to update a client's address, phone, email, beneficiaries, trustee or trusted contact - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. Most fields are required once their section opens.</Para>
        <Bullets items={[
          <><strong>The client&rsquo;s first and last name</strong> and the household they belong to.</>,
          <><strong>The new details</strong> - full address, phone number or email as applicable.</>,
          <><strong>For each beneficiary</strong>: type, relationship to the owner, percent, first and last name, phone and email, plus DOB and SSN if you have them, and which accounts it applies to (or ALL).</>,
          <><strong>For a trustee or trusted contact</strong>: first and last name, phone, email and relationship to the owner.</>,
          <><strong>The signed forms and supporting documents</strong>, ready to upload. Missing or incomplete documents may result in a NIGO.</>,
        ]} />
        <Callout>
          One case can carry several kinds of change for the same client - tick every box that applies rather than
          opening a separate case for each.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="operations"
        before={(
          <Para>
            Your answers are written onto the case. If you entered beneficiaries, FSC also creates one beneficiary
            record per block, linked to the case and the household.
          </Para>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe estate-planning considerations for
        beneficiary designations or the custodian&rsquo;s own requirements for each kind of change.
      </Footnote>
    </GuidePage>
  );
}
